import { TextField, Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { InputAdornment } from "@mui/material";
import { server } from "../common";
import { AxiosError } from "axios";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useSelectAccess } from "../store/stateFunctions";

const initialValues = {
  title: "",
  image: null,
  type: "photo",
  video_url: "",
  description: "",
  sequence: 0,
};

const swalalert = withReactContent(Swal);

const AddGallery = () => {
  const user = useSelectAccess("Super Admin") || {};
  const token = user.authToken || localStorage.getItem("superadminToken");

  const [show, setShow] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleClose = () => {
    setShow(false);
    setIsEditMode(false);
    setSelectedData(null);
    formik.resetForm();
  };
  const handleShow = () => {
    setIsEditMode(false);
    setSelectedData(null);
    formik.resetForm();
    setShow(true);
  };

  const handleShowEdit = (data) => {
    setSelectedData(data);
    setIsEditMode(true);
    formik.setValues({
      title: data.title,
      image: null, // security reasons
      type: data.type || "photo",
      video_url: data.video_url || "",
      description: data.description || "",
      sequence: data.sequence || 0,
    });
    setShow(true);
  };

  useEffect(() => {
    getData();
  }, []);

  // Get
  const getData = () => {
    server
      .get("/gallery/getallgallery")
      .then((response) => {
        if (response.status === 200) {
          setData(response.data);
        }
      })
      .catch((error) => {
        toast.error("Failed to load gallery");
      });
  };

  // Delete
  const deleteGallery = (data) => {
    swalalert
      .fire({
        title: "Delete Confirmation!",
        text: `Are you sure you want to delete "${data.title}" ?`,
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      })
      .then((res) => {
        if (res.isConfirmed) {
          server
            .delete(`/gallery/deletegallery/${data.id}`, {
              headers: { Authorization: token },
            })
            .then(() => {
              toast.success("Gallery deleted");
              getData();
            })
            .catch(() => toast.error("Delete failed"));
        }
      });
  };

  // Update
  const handleUpdate = (values) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("type", values.type);
    formData.append("sequence", values.sequence || 0);

    if (values.type === "photo") {
      if (values.image) {
        formData.append("image", values.image);
      }
    } else {
      formData.append("video_url", values.video_url || selectedData.video_url);
      formData.append("description", values.description || "");
    }

    server
      .put(`/gallery/updategallery/${selectedData.id}`, formData, {
        headers: {
          Authorization: token,
          // Don't set Content-Type manually for FormData - axios will set it automatically with boundary
        },
      })
      .then(() => {
        toast.success("Gallery updated");
        getData();
        handleClose();
      })
      .catch(() => toast.error("Update failed"));
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      title: Yup.string().required("Enter a title"),
      image: Yup.mixed().nullable().when("type", {
        is: "photo",
        then: (schema) => schema.required("Image is required for photos"),
      }),
      video_url: Yup.string().when("type", {
        is: "video",
        then: (schema) => schema.required("Video URL is required for videos"),
      }),
      sequence: Yup.number().min(0, "Sequence must be 0 or greater"),
    }),
    onSubmit: (values) => {
      if (isEditMode) {
        handleUpdate(values);
      } else {
        handleAdd(values);
      }
    },
  });

  const handleAdd = (values) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("type", values.type);
    formData.append("sequence", values.sequence || 0);

    if (values.type === "photo") {
      if (!values.image) {
        toast.error("Image is required for photos");
        return;
      }
      formData.append("image", values.image);
    } else {
      if (!values.video_url) {
        toast.error("Video URL is required for videos");
        return;
      }
      formData.append("video_url", values.video_url);
      formData.append("description", values.description || "");
    }

    server
      .post("/gallery/addgallery", formData, {
        headers: { 
          Authorization: token,
          // Don't set Content-Type manually for FormData - axios will set it automatically with boundary
        },
      })
      .then(() => {
        toast.success("Gallery added");
        getData();
        handleClose();
      })
      .catch(() => toast.error("Add failed"));
  };

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between mb-3">
        <h5>Previous Gallery</h5>
        <Button variant="contained" onClick={handleShow}>
          Add Gallery
        </Button>
      </div>

      {/* Modal */}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Edit Gallery" : "Add Gallery"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={formik.handleSubmit}>
            <div className="mb-4 p-3 border rounded bg-light">
              <label className="form-label fw-bold mb-3 d-block">Select Type *</label>
              <div className="d-flex gap-4">
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    id="type-photo"
                    name="type"
                    value="photo"
                    checked={formik.values.type === "photo"}
                    onChange={(e) => {
                      formik.setFieldValue("type", e.target.value);
                    }}
                    className="form-check-input"
                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                  />
                  <label htmlFor="type-photo" className="form-check-label fw-bold" style={{ cursor: "pointer", fontSize: "16px" }}>
                    📷 Photo
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    id="type-video"
                    name="type"
                    value="video"
                    checked={formik.values.type === "video"}
                    onChange={(e) => {
                      formik.setFieldValue("type", e.target.value);
                    }}
                    className="form-check-input"
                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                  />
                  <label htmlFor="type-video" className="form-check-label fw-bold text-primary" style={{ cursor: "pointer", fontSize: "16px" }}>
                    🎥 Video
                  </label>
                </div>
              </div>
            </div>

            <TextField
              name="title"
              fullWidth
              margin="dense"
              label="Title"
              value={formik.values.title}
              onChange={formik.handleChange}
            />
            {formik.errors.title && (
              <p className="text-danger">{formik.errors.title}</p>
            )}

            {formik.values.type === "photo" ? (
              <div className="mt-3">
                <label className="form-label fw-bold">Upload Image *</label>
                <input
                  type="file"
                  name="image"
                  accept=".jpg, .jpeg, .png"
                  className="form-control mt-2"
                  onChange={(event) => {
                    formik.setFieldValue("image", event.currentTarget.files[0]);
                  }}
                />
                {formik.errors.image && (
                  <p className="text-danger mt-1">{formik.errors.image}</p>
                )}
              </div>
            ) : (
              <div className="mt-3">
                <div className="alert alert-info">
                  <strong>Video Option Selected:</strong> Please provide YouTube video URL
                </div>
                <TextField
                  name="video_url"
                  fullWidth
                  margin="dense"
                  label="YouTube Video URL *"
                  value={formik.values.video_url}
                  onChange={formik.handleChange}
                  placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                  className="mt-2"
                />
                {formik.errors.video_url && (
                  <p className="text-danger">{formik.errors.video_url}</p>
                )}
                <TextField
                  name="description"
                  fullWidth
                  margin="dense"
                  label="Description (Optional)"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  multiline
                  rows={3}
                  className="mt-2"
                />
              </div>
            )}

            <TextField
              name="sequence"
              fullWidth
              margin="dense"
              label="Sequence (Order)"
              type="number"
              value={formik.values.sequence}
              onChange={formik.handleChange}
              inputProps={{ min: 0 }}
            />
            {formik.errors.sequence && (
              <p className="text-danger">{formik.errors.sequence}</p>
            )}

            <div className="mt-3">
              <Button type="submit" variant="contained">
                {isEditMode ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Table */}
      <table className="table table-striped border mt-3">
        <thead>
          <tr>
            <th>Sr. No</th>
            <th>Type</th>
            <th>Title</th>
            <th>Image/Video</th>
            <th>Sequence</th>
            <th>Update</th>
            <th>Delete</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={item.id}>
              <td>{index + 1}</td>
              <td>
                <span className={`badge ${item.type === 'video' ? 'bg-primary' : 'bg-success'}`}>
                  {item.type === 'video' ? 'Video' : 'Photo'}
                </span>
              </td>
              <td>{item.title}</td>
              <td>
                {item.type === 'photo' && item.image ? (
                  <a href={item.image} target="_blank" rel="noreferrer">
                    <img src={`${item.image}`} alt="" width="50" height="50" />
                  </a>
                ) : item.type === 'video' && item.video_url ? (
                  <a href={item.video_url} target="_blank" rel="noreferrer">
                    Video Link
                  </a>
                ) : (
                  'N/A'
                )}
              </td>
              <td>{item.sequence || 0}</td>
              <td>
                <Button size="small" onClick={() => handleShowEdit(item)}>
                  <AiFillEdit />
                </Button>
              </td>
              <td>
                <Button
                  color="error"
                  size="small"
                  onClick={() => deleteGallery(item)}
                >
                  <AiFillDelete />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AddGallery;
